import { fabric } from 'fabric';
import { Subject } from 'rxjs';
import { DrawingPenOption, DrawingTool } from 'viewer';

function penWidthRelativeToViewportScale(
  width: number,
  viewportScale: number
): number {
  // 최소 두께 0.5로 변경
  return Math.max(width / viewportScale, 0.5);
}

interface Size {
  width: number;
  height: number;
}

export interface DrawingHistoryChangeEvent {
  canUndo: boolean;
  canRedo: boolean;
}

const ELEMENT_STYLE = {
  position: 'absolute',
  top: '0',
  right: '0',
  bottom: '0',
  left: '0',
  'pointer-events': 'auto',
} as { [key: string]: string };

export class PDFDrawableOverlayView {
  public readonly element: HTMLDivElement;

  public readonly canvas: fabric.Canvas;
  private readonly _pencilBrush: fabric.PencilBrush;

  private _eraserStarted = false;
  private _erasedObjects: fabric.Object[] = [];

  private _isEraserMode = false;
  private _isTextMode = false;
  private _isSelectionMode = false;
  private _loading = false;

  // 텍스트 입력 완료 후 첫 번째 배경 클릭 상태 추적
  private _textInputCompleted = false;

  // 롱 프레스 관련 변수
  private _longPressTimer: number | null = null;
  private _longPressTarget: fabric.Object | null = null;
  private _isControlLongPressed = false; // 컨트롤 롱 프레스 상태 추가

  // 현재 텍스트 설정
  private _currentTextOptions: {
    color: string;
    fontSize: number;
    fontFamily: string;
  } = {
    color: '#000000',
    fontSize: 16,
    // fontFamily: 'Noto Sans KR',
    fontFamily: 'pretendard variable',
  };

  // 현재 툴 옵션 저장
  private _currentToolOption?: DrawingPenOption;

  private readonly _drawingEnd$ = new Subject<void>();
  public readonly drawingEnd$ = this._drawingEnd$.asObservable();

  private readonly _historyChange$ = new Subject<DrawingHistoryChangeEvent>();
  public readonly historyChange$ = this._historyChange$.asObservable();

  public undoStack = [];
  public redoStack = [];

  private _penStarted = false;
  private _wasEmpty = false;

  // 텍스트 객체 선택 콜백들
  public onTextObjectSelected?: (textData: {
    text: string;
    color: string;
    fontSize: number;
    fontFamily: string;
  }) => void;
  public onTextObjectDeselected?: () => void;

  // 툴 변경 콜백 추가
  public onToolChanged?: (tool: DrawingTool, option?: DrawingPenOption) => void;

  constructor() {
    this.element = document.createElement('div');

    for (const prop in ELEMENT_STYLE) {
      this.element.style.setProperty(prop, ELEMENT_STYLE[prop]);
    }

    // 터치 이벤트 개선
    this.element.addEventListener(
      'touchstart',
      (event) => {
        // 펜 입력이 아닌 일반 터치는 처리
        if (event.touches[0].radiusX === 0) {
          event.stopPropagation();
        }
      },
      { passive: false }
    );

    this.element.addEventListener('click', (event) => {
      if (this._penStarted) {
        event.stopPropagation();
      }

      // 텍스트 모드일 때는 페이지 클릭 이벤트 전파 중지 (위아래 메뉴 방지)
      if (this._isTextMode) {
        event.stopPropagation();
        event.preventDefault();
      }
    });

    this.element.addEventListener('pointerdown', (event) => {
      this._penStarted = event.pointerType === 'pen';
      if (event.pointerType === 'pen') {
        (event as any).bukvEventHandled = true;
      }
    });

    const canvas = document.createElement('canvas');

    this.element.appendChild(canvas);

    this.canvas = new fabric.Canvas(canvas, {
      perPixelTargetFind: true,
      allowTouchScrolling: true, // 터치 스크롤링 비활성화하여 객체 조작 개선
      selection: true,
      selectionColor: 'rgba(0, 0, 0, 0)',
      selectionDashArray: [2],
      selectionBorderColor: 'rgb(170, 170, 170)',
      selectionLineWidth: 1,
      preserveObjectStacking: true,
      hoverCursor: 'default',
      moveCursor: 'move',
      // 터치 지원 개선
      // enablePointerEvents: true,
      stopContextMenu: true,
      fireRightClick: false,
      fireMiddleClick: false,
      // 터치에서 더 큰 터치 영역 설정
      targetFindTolerance: 10,
    });

    this.canvas.on('mouse:down', (event) => {
      // 다시 텍스트 편집 모드 터치시 커서 생기는 로직
      const target = event.target;
      if (target && target.type === 'textbox') {
        // 커서를 다시 보이게 함
        (target as any).set('cursorWidth', 2);
        this.canvas.requestRenderAll();
      }

      this._eraserStarted = this._isEraserMode;

      if (this._eraserStarted) {
        this._erasedObjects = [];
        this.canvas.targetFindTolerance = 15; // 지우개 모드에서 더 큰 터치 영역
        this.canvas.offHistory();
      }

      // 텍스트 모드에서 클릭 시 처리
      if (this._isTextMode && !this._isSelectionMode && event.pointer) {
        // 현재 편집 중인 텍스트가 있는지 확인
        const activeObject = this.canvas.getActiveObject();
        const isEditingText =
          activeObject &&
          activeObject.type === 'textbox' &&
          (activeObject as any).isEditing;

        if (event.target && event.target.type === 'textbox') {
          // 기존 텍스트 클릭 시: 선택 표시 후 편집 모드로 진입하지 않음 (잠시 대기)
          this.canvas.setActiveObject(event.target);
          this.canvas.requestRenderAll();

          // 편집 모드 진입은 selection:created 이벤트에서 처리
        } else if (!event.target) {
          // 배경 클릭 시 처리
          if (isEditingText) {
            // 편집 중인 텍스트가 있으면 편집 종료 (text:editing:exited 이벤트에서 처리됨)
            (activeObject as any).exitEditing();
          } else if (this._textInputCompleted) {
            // 텍스트 입력 완료 후 첫 번째 배경 클릭: 아무것도 하지 않음 (텍스트 모드 유지)
            this._textInputCompleted = false; // 상태 리셋
          } else {
            // 일반적인 경우: 새로운 텍스트 추가
            const pointer = this.canvas.getPointer(event.e);
            this._addTextAtPosition(pointer.x, pointer.y);
          }
        }
      }

      // 롱 프레스 시작 (텍스트 객체에서만)
      if (event.target && event.target.type === 'textbox') {
        this._longPressTarget = event.target;
        this._longPressTimer = window.setTimeout(() => {
          if (this._longPressTarget) {
            this.canvas.setActiveObject(this._longPressTarget);
            (this._longPressTarget as any).enterEditing();
            (this._longPressTarget as any).selectAll();
            this._longPressTarget = null;
          }
        }, 800); // 800ms 롱 프레스
      }
    });

    // 더블클릭 이벤트 제거 (화면 확대와 충돌 방지)
    // 대신 다른 방법들로 텍스트 편집 제공:
    // 1. Enter 키로 편집 모드 진입
    // 2. 텍스트 모드에서 기존 텍스트 클릭으로 편집 모드 진입

    // 텍스트 객체 선택 시 편집 모드 진입 이벤트
    const selectionCreated = (event: any): void => {
      const activeObject = event.selected?.[0];
      if (activeObject && activeObject.type === 'textbox') {
        // 텍스트 모드와 selection 모드에서 텍스트 객체 선택 시 색상 변경 UI 표시
        if (
          (this._isTextMode || this._isSelectionMode) &&
          this.onTextObjectSelected
        ) {
          const textObject = activeObject as any;

          this.resizeTextWidth(textObject);

          this.onTextObjectSelected({
            text: textObject.text,
            color: textObject.fill || '#000000',
            fontSize: Math.round(
              (textObject.fontSize || 16) * this.viewportScale
            ),
            fontFamily:
              this._extractFontFamily(textObject.fontFamily) ||
              'pretendard variable',
          });
        }
      }
    };

    this.canvas.on('selection:created', selectionCreated);

    // selection:updated 이벤트 추가 - 이미 선택된 상태에서 다른 텍스트를 선택할 때
    this.canvas.on('selection:updated', selectionCreated);

    // selection:cleared 이벤트 추가
    this.canvas.on('selection:cleared', () => {
      if (
        (this._isTextMode || this._isSelectionMode) &&
        this.onTextObjectDeselected
      ) {
        this.onTextObjectDeselected();
      }
    });

    // 텍스트 편집 종료 시 빈 텍스트 제거
    this.canvas.on('text:editing:exited', (event) => {
      const target = event.target;
      if (target && target.type === 'textbox') {
        const text = (target as any).text?.trim();
        if (!text) {
          // 빈 텍스트인 경우 제거 (기존 동작)
          this.canvas.offHistory();
          this.canvas.remove(target);
          this.canvas.requestRenderAll();
          this.canvas.onHistory(true);
        } else {
          // 한 글자라도 입력된 경우: 텍스트 모드 유지하고 상태 플래그 설정
          this._textInputCompleted = true;
          // 기존의 펜 모드 전환 로직 제거 (주석 처리)
          // if (this._isTextMode && this.onToolChanged) {
          //   this.onToolChanged('pen', this._currentToolOption);
          //   this._drawingEnd$.next(); // 드로잉 종료 이벤트 발생
          // }
        }
      }
    });

    this.canvas.on('mouse:up', (event) => {
      // 롱 프레스 타이머 해제
      if (this._longPressTimer) {
        clearTimeout(this._longPressTimer);
        this._longPressTimer = null;
        this._longPressTarget = null;
      }

      if (!this._eraserStarted) {
        return;
      }

      // 다양한 방법으로 타겟 찾기 시도
      let target = this.canvas.findTarget(event.e, true);

      // findTarget이 null이면 다른 방법 시도
      if (!target) {
        // 포인터 좌표를 직접 사용해서 찾기
        const pointer = this.canvas.getPointer(event.e);

        // 모든 객체를 순회하면서 포인터가 객체 내부에 있는지 확인
        const objects = this.canvas.getObjects();

        for (let i = objects.length - 1; i >= 0; i--) {
          const obj = objects[i];
          if (
            obj.containsPoint &&
            obj.containsPoint(new fabric.Point(pointer.x, pointer.y))
          ) {
            target = obj;
            break;
          }
        }
      }

      if (target && !this._erasedObjects.includes(target)) {
        this._erasedObjects.push(target);
        target.opacity = 0.3;
        target.evented = false;
      }

      this._eraserStarted = false;

      this.canvas.targetFindTolerance = 10; // 원래 값으로 복원

      if (this._erasedObjects.length > 0) {
        this.canvas.remove(...this._erasedObjects);
        this.canvas.requestRenderAll();
        this.canvas.fire('eraser:end');
        this.canvas.onHistory(); // 지우개 동작을 히스토리에 추가
      } else {
        this.canvas.onHistory(true); // 빈 동작이면 히스토리에 추가하지 않음
      }
    });

    // 마우스 이동 시에도 롱 프레스 취소 (드래그 시작 시)
    this.canvas.on('mouse:move', (event) => {
      // 롱 프레스 중에 마우스가 움직이면 취소
      if (this._longPressTimer) {
        clearTimeout(this._longPressTimer);
        this._longPressTimer = null;
        this._longPressTarget = null;
      }

      if (!this._eraserStarted) {
        return;
      }

      // 다양한 방법으로 타겟 찾기 시도
      let target = this.canvas.findTarget(event.e, true);

      // findTarget이 null이면 다른 방법 시도
      if (!target) {
        // 포인터 좌표를 직접 사용해서 찾기
        const pointer = this.canvas.getPointer(event.e);

        // 모든 객체를 순회하면서 포인터가 객체 내부에 있는지 확인
        const objects = this.canvas.getObjects();

        for (let i = objects.length - 1; i >= 0; i--) {
          const obj = objects[i];
          if (
            obj.containsPoint &&
            obj.containsPoint(new fabric.Point(pointer.x, pointer.y))
          ) {
            target = obj;
            break;
          }
        }
      }

      if (target && !this._erasedObjects.includes(target)) {
        this._erasedObjects.push(target);
        target.opacity = 0.3;
        target.evented = false;
        this.canvas.requestRenderAll();
      }
    });

    // 더블클릭으로 텍스트 편짓 모드 진입
    this.canvas.on('mouse:dblclick', (event) => {
      if (event.target && event.target.type === 'textbox') {
        // 텍스트 객체를 더블클릭하면 편집 모드로 진입
        this.canvas.setActiveObject(event.target);
        setTimeout(() => {
          (event.target as any).enterEditing();
          (event.target as any).selectAll();
        }, 50);
        event.e.preventDefault();
        event.e.stopPropagation();
      }
    });

    [
      'object:added',
      'object:removed',
      'object:modified',
      'history:undo',
      'history:redo',
      'eraser:end',
    ].forEach((eventName) => {
      this.canvas.on(eventName, () => {
        if (this.canvas.historyProcessing || this._loading) {
          return;
        }

        this._drawingEnd$.next();
      });
    });

    [
      'history:undo',
      'history:redo',
      'history:clear',
      'history:append',
      'history:reset',
    ].forEach((eventName) => {
      this.canvas.on(eventName, () => {
        // 히스토리 변경 후 텍스트 객체 스타일 복원
        if (eventName === 'history:undo' || eventName === 'history:redo') {
          this._restoreTextObjectStyles();
        }

        this._historyChange$.next({
          canUndo: this.canUndo,
          canRedo: this.canRedo,
        });
      });
    });

    this._pencilBrush = new fabric.PencilBrush(this.canvas);
  }

  get canUndo(): boolean {
    return this.canvas.historyUndo.length > 0;
  }

  get canRedo(): boolean {
    return this.canvas.historyRedo.length > 0;
  }

  get viewportScale(): number {
    return this.canvas.viewportTransform?.[0] ?? 1;
  }

  set viewportScale(scale: number) {
    const prevScale = this.viewportScale;
    const originalPenWidth = this._pencilBrush.width * prevScale;

    this._pencilBrush.width = penWidthRelativeToViewportScale(
      originalPenWidth,
      scale
    );

    this.canvas.setViewportTransform([scale, 0, 0, scale, 0, 0]);
  }

  //
  private resizeTextWidth(textObject: fabric.Textbox): void {
    const lineCount = textObject.textLines.length;

    if (textObject.text !== '') {
      let maxWidth = 10;
      for (let i = 0; i < lineCount; i++) {
        const lineWidth = textObject.getLineWidth(i);
        if (lineWidth > maxWidth) {
          maxWidth = lineWidth;
        }
      }
      textObject.width = maxWidth;
    }

    textObject.set({
      text: textObject.text?.trimEnd() || textObject.text,
    });
  }

  private _extractFontFamily(fontFamily: string | undefined): string {
    if (!fontFamily) {
      return 'pretendard variable';
    }

    // ', sans-serif', ', serif', ', monospace' 등을 제거하고 첫 번째 폰트만 추출
    const cleanFontFamily = fontFamily.split(',')[0].trim();

    // 따옴표 제거 (예: "Arial" -> Arial)
    return cleanFontFamily.replace(/['"]/g, '');
  }

  getDrawingSize(): Size {
    const currScale = this.viewportScale;

    return {
      width: Math.round(this.canvas.getWidth() / currScale),
      height: Math.round(this.canvas.getHeight() / currScale),
    };
  }

  getDrawing(): string | null | undefined {
    if (!this.canvas.isEmpty()) {
      return this.canvas.toSVG();
    }

    // 현재 canvas가 비어있고 원래 비어있었던 경우를 판단
    if (this._wasEmpty) {
      return null;
    } else {
      return undefined;
    }
  }

  isEmpty(): boolean {
    return this.canvas.isEmpty();
  }

  load(pageSize: Size, svg?: string): void {
    this._loading = true;
    this._wasEmpty = !svg;

    this.canvas.offHistory();
    this.canvas.clear();

    if (svg) {
      //svg의 기존 data-* 속성 제거 (data-object-infos는 유지)
      const svgWithoutDataAttributes = svg.replace(
        /data-(?!object-infos)[^=]*="[^"]*"/g,
        ''
      );

      fabric.loadSVGFromString(svgWithoutDataAttributes, (objects, options) => {
        // SVG에서 data-object-infos 속성 파싱
        let objectInfos: any[] = [];
        try {
          const svgElement = new DOMParser().parseFromString(
            svg,
            'image/svg+xml'
          );
          const svgTag = svgElement.querySelector('svg');
          if (svgTag && svgTag.hasAttribute('data-object-infos')) {
            const encodedObjectInfos =
              svgTag.getAttribute('data-object-infos') || '';
            objectInfos = JSON.parse(atob(encodedObjectInfos));
          }
        } catch (e) {
          // console.warn('Failed to parse object infos:', e);
        }

        // objects에 커스텀 속성을 먼저 넣어주기
        objects.forEach((object, index) => {
          // objectInfos 배열에서 해당 인덱스의 정보 가져오기
          if (index < objectInfos.length && objectInfos[index]) {
            const info = objectInfos[index];
            // 객체에 커스텀 속성 추가
            (object as any).__customInfo = info;
          }

          // 커스텀 속성을 확인하여 편짓 가능한 텍스트로 복원
          if (this._shouldConvertToEditableTextWithCustomInfo(object)) {
            const editableText =
              this._convertToEditableTextWithCustomInfo(object);
            if (editableText) {
              this.canvas.add(editableText);
              return;
            }
          }

          // 텍스트 객체는 항상 선택 가능하도록 설정
          if (object.type === 'textbox') {
            object.selectable = true;
            object.evented = true;
            // 로드된 텍스트 객체에도 스타일 및 컨트롤 활성화
            (object as any).set({
              // 좌표 기준점을 명시적으로 설정하여 위치 드리프트 방지
              originX: (object as any).originX || 'left',
              originY: (object as any).originY || 'top',
              // 텍스트 전용: 확대축소 및 회전 컨트롤 활성화
              hasControls: true,
              hasBorders: true,
              hasRotatingPoint: true,
              lockUniScaling: false, // 비율 고정 해제하여 자유로운 크기 조정 허용
              editingBorderColor: '#2196F3',
              cursorColor: '#2196F3',
              cursorWidth: 2,
              borderColor: '#2196F3',
              cornerColor: '#2196F3',
              cornerSize: 8,
              transparentCorners: false,
              // 터치 지원 개선
              touchCornerSize: 24,
            });
          }

          this.canvas.add(object);
        });

        const scale = (fabric.util as any).findScaleToFit(
          { width: options.width, height: options.height },
          pageSize
        );

        this.viewportScale = scale || 1;
        this.canvas.renderAll();

        this.canvas.setDimensions(pageSize);
      });
    } else {
      this.viewportScale = 1;
      this.canvas.renderAll();
      this.canvas.setDimensions(pageSize);
    }

    this.canvas.resetHistory();

    this._loading = false;
  }

  clearSelection(): void {
    this.canvas.discardActiveObject();
    this.canvas.requestRenderAll();
  }

  clearDrawing(): void {
    // clear 함수가 객체를 하나씩 지우기 때문에 clear 함수를 실행하는 동안에는 히스토리를 기억하지 않도록 해야함
    this.canvas.offHistory();

    this.canvas.clear();

    this.canvas.onHistory();
  }

  undo(): void {
    this.canvas.undo();
  }

  redo(): void {
    this.canvas.redo();
  }

  /**
   * 커스텀 정보가 포함된 객체가 편짓 가능한 텍스트인지 확인
   */
  private _shouldConvertToEditableTextWithCustomInfo(
    object: fabric.Object
  ): boolean {
    // 텍스트 타입이 아니면 변환하지 않음
    if (object.type !== 'text') {
      return false;
    }

    // 객체에 커스텀 정보가 있는지 확인
    const customInfo = (object as any).__customInfo;
    if (customInfo && customInfo.type) {
      return true;
    }

    return false;
  }

  /**
   * 커스텀 정보를 사용하여 편짓 가능한 텍스트로 변환 (필기모드를 껏다가 킬때 객체마다 여러번 호출됨)
   */
  private _convertToEditableTextWithCustomInfo(
    object: fabric.Object
  ): fabric.Textbox | null {
    if (object.type !== 'text') {
      return null;
    }

    const customInfo = (object as any).__customInfo;
    if (!customInfo || !customInfo.originalText) {
      return null;
    }

    // 원본 텍스트 디코딩
    const originalText = decodeURIComponent(atob(customInfo.originalText));

    // 공통 속성
    const commonProps = {
      left: customInfo.fabricLeft || (object as any).left,
      top: customInfo.fabricTop || (object as any).top,
      originX: customInfo.originX || 'left',
      originY: customInfo.originY || 'top',
      width: customInfo.textboxWidth || 200,
      fill: (object as any).fill,
      fontFamily: (object as any).fontFamily,
      fontSize: (object as any).fontSize,
      textAlign: (object as any).textAlign,
      scaleX: customInfo.scaleX || (object as any).scaleX || 1,
      scaleY: customInfo.scaleY || (object as any).scaleY || 1,
      angle: customInfo.angle || (object as any).angle || 0,
      selectable: true,
      editable: true,
      evented: true,
      hasControls: true,
      hasBorders: true,
      hasRotatingPoint: true,
      lockUniScaling: false,
      editingBorderColor: '#0047FF',
      cursorColor: '#282828',
      cursorWidth: 2,
      borderColor: '#0047FF',
      cornerColor: '#0047FF',
      cornerSize: 3,
      transparentCorners: false,
      lockMovementX: true, // X축 이동 잠금
      lockMovementY: true, // Y축 이동 잠금
      splitByGrapheme: true,
    };

    // 항상 Textbox로 생성
    const editableText = new fabric.Textbox(originalText, {
      ...commonProps,
    });

    // 좌우 중앙 컨트롤만 활성화하고 위아래 컨트롤 비활성화
    editableText.setControlsVisibility({
      mt: false, // top middle (위 중앙)
      mb: false, // bottom middle (아래 중앙)
      ml: true, // left middle (왼쪽 중앙)
      mr: true, // right middle (오른쪽 중앙)
      mtr: false, // rotate (회전)
      tl: true, // top left (왼쪽 위)
      tr: true, // top right (오른쪽 위)
      bl: true, // bottom left (왼쪽 아래)
      br: true, // bottom right (오른쪽 아래)
    });

    // 좌표 재계산
    editableText.setCoords();

    return editableText;
  }

  /**
   * 일반 텍스트 객체를 편짓 가능한 Textbox로 변환
   */
  private _convertToEditableText(
    object: fabric.Object,
    svg: string
  ): fabric.Textbox | null {
    if (object.type !== 'text') {
      return null;
    }

    const textObj = object as any;

    // SVG에서 원본 텍스트 정보 추출
    const originalTextInfo = this._extractOriginalTextInfo(svg, textObj);
    const finalText = originalTextInfo.text || textObj.text || '';
    const textboxWidth = originalTextInfo.width || textObj.width || 200;

    // 좌표 결정: 저장된 절대 좌표가 있으면 우선 사용, 없으면 보정 로직 적용
    let finalLeft, finalTop;
    const targetOriginX = originalTextInfo.originX || 'left';
    const targetOriginY = originalTextInfo.originY || 'top';

    if (
      originalTextInfo.fabricLeft !== undefined &&
      originalTextInfo.fabricTop !== undefined
    ) {
      // 저장된 절대 좌표 사용 (가장 정확함)
      finalLeft = originalTextInfo.fabricLeft;
      finalTop = originalTextInfo.fabricTop;
    } else {
      // 기존 보정 로직 사용
      finalLeft = textObj.left;
      finalTop = textObj.top;

      const currentOriginX = textObj.originX || 'left';
      const currentOriginY = textObj.originY || 'top';

      // originX가 다른 경우 보정
      if (currentOriginX !== targetOriginX) {
        if (currentOriginX === 'center' && targetOriginX === 'left') {
          const estimatedWidth =
            finalText.length * (textObj.fontSize || 16) * 0.6;
          finalLeft = textObj.left - estimatedWidth / 2;
        } else if (currentOriginX === 'left' && targetOriginX === 'center') {
          const estimatedWidth =
            finalText.length * (textObj.fontSize || 16) * 0.6;
          finalLeft = textObj.left + estimatedWidth / 2;
        }
      }

      // originY가 다른 경우 보정
      if (currentOriginY !== targetOriginY) {
        if (currentOriginY === 'baseline' && targetOriginY === 'top') {
          finalTop = textObj.top - (textObj.fontSize || 16);
        } else if (currentOriginY === 'top' && targetOriginY === 'baseline') {
          finalTop = textObj.top + (textObj.fontSize || 16);
        }
      }
    }

    // 공통 속성
    const commonProps = {
      left: finalLeft,
      top: finalTop,
      originX: targetOriginX, // 목표 좌표 기준점 사용
      originY: targetOriginY, // 목표 좌표 기준점 사용
      fill: textObj.fill,
      fontFamily: textObj.fontFamily,
      fontSize: textObj.fontSize,
      fontWeight: textObj.fontWeight,
      fontStyle: textObj.fontStyle,
      textAlign: textObj.textAlign,
      angle: textObj.angle,
      scaleX: textObj.scaleX,
      scaleY: textObj.scaleY,
      selectable: true,
      editable: true,
      evented: true,
      // 텍스트 전용: 확대축소 및 회전 컨트롤 활성화
      hasControls: true,
      hasBorders: true,
      hasRotatingPoint: true,
      lockUniScaling: false, // 비율 고정 해제하여 자유로운 크기 조정 허용
      // 터치 디바이스에서 더 나은 편짓 경험
      editingBorderColor: '#2196F3',
      cursorColor: '#2196F3',
      cursorWidth: 2,
      // 선택 시 더 명확한 테두리
      borderColor: '#2196F3',
      cornerColor: '#2196F3',
      cornerSize: 8,
      transparentCorners: false,
      // 터치 지원 개선
      touchCornerSize: 24,
    };

    // 항상 Textbox로 생성
    const editableText = new fabric.Textbox(finalText, {
      ...commonProps,
      width: textboxWidth,
    });

    // 좌표 재계산 - 중요!
    editableText.setCoords();

    return editableText;
  }

  /**
   * SVG에서 원본 텍스트 정보 추출
   */
  private _extractOriginalTextInfo(
    svg: string,
    textObj: any
  ): {
    text?: string;
    isTextbox: boolean;
    width?: number;
    originX?: string;
    originY?: string;
    fabricLeft?: number;
    fabricTop?: number;
  } {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svg, 'image/svg+xml');
    const textElements = svgDoc.querySelectorAll('text');

    for (let i = 0; i < textElements.length; i++) {
      const textElement = textElements[i];

      // 최상위 svg 태그의 data-object-infos 속성에서 인덱스 기반으로 원본 텍스트 정보 추출
      const svgElement = textElement.closest('svg');
      if (svgElement && svgElement.hasAttribute('data-object-infos')) {
        try {
          const encodedObjectInfos =
            svgElement.getAttribute('data-object-infos') || '';
          const objectInfos = JSON.parse(atob(encodedObjectInfos));

          // 현재 text 요소의 인덱스 찾기
          const allTextElements = svgElement.querySelectorAll('text');
          let currentIndex = -1;
          for (let j = 0; j < allTextElements.length; j++) {
            if (allTextElements[j] === textElement) {
              currentIndex = j;
              break;
            }
          }

          // 인덱스가 유효하고 해당 위치에 텍스트 정보가 있는 경우
          if (currentIndex >= 0 && currentIndex < objectInfos.length) {
            const textInfo = objectInfos[currentIndex];
            if (textInfo && textInfo.type && textInfo.originalText) {
              const decodedText = decodeURIComponent(
                atob(textInfo.originalText)
              );

              const isTextbox = textInfo.type === 'textbox';
              const width = textInfo.textboxWidth;
              const originX = textInfo.originX || 'left';
              const originY = textInfo.originY || 'top';
              const fabricLeft = textInfo.fabricLeft;
              const fabricTop = textInfo.fabricTop;

              return {
                text: decodedText,
                isTextbox,
                width,
                originX,
                originY,
                fabricLeft,
                fabricTop,
              };
            }
          }
        } catch (e) {
          // base64 디코딩 또는 JSON 파싱 실패 시 continue
        }
      }
    }

    return { isTextbox: false };
  }

  private _addTextAtPosition(x: number, y: number): void {
    // 현재 텍스트 설정 적용
    const scaledFontSize = Math.max(
      this._currentTextOptions.fontSize / this.viewportScale,
      12
    );

    // 텍스트 입력 객체 생성 (Textbox 사용)
    const textbox = new fabric.Textbox('', {
      left: x,
      top: y,
      originX: 'left', // 좌표 기준점을 명시적으로 설정
      originY: 'top', // 좌표 기준점을 명시적으로 설정
      width: 200,
      fill: this._currentTextOptions.color,
      // fontFamily: this._currentTextOptions.fontFamily,
      fontFamily: 'pretendard variable',
      fontSize: scaledFontSize,
      textAlign: 'left',
      selectable: true,
      editable: true,
      evented: true,
      hasControls: true,
      hasBorders: true,
      hasRotatingPoint: true,
      lockUniScaling: false, // 비율 고정 해제하여 자유로운 크기 조정 허용
      editingBorderColor: '#0047FF',
      cursorColor: '#282828',
      cursorWidth: 2,
      borderColor: '#0047FF',
      cornerColor: '#0047FF',
      cornerSize: 3,
      transparentCorners: false,
      lockMovementX: true, // X축 이동 잠금
      lockMovementY: true, // Y축 이동 잠금
      splitByGrapheme: true,
    } as any);

    // 커스텀 컨트롤 추가
    this._addCustomMoveControl(textbox);
    fabric.Object.prototype.controls.tl.offsetX = -0.5;
    fabric.Object.prototype.controls.tl.offsetY = -0.4;
    fabric.Object.prototype.controls.bl.offsetX = -0.5;
    fabric.Object.prototype.controls.bl.offsetY = 0.4;
    fabric.Textbox.prototype.controls.ml.offsetX = -0.5;

    fabric.Object.prototype.controls.tr.offsetX = 0.5;
    fabric.Object.prototype.controls.tr.offsetY = -0.4;
    fabric.Object.prototype.controls.br.offsetX = 0.5;
    fabric.Object.prototype.controls.br.offsetY = 0.4;
    fabric.Textbox.prototype.controls.mr.offsetX = 0.5;
    // Textbox 클래스의 ml과 mr 컨트롤에만 적용

    // 좌우 중앙 컨트롤만 활성화하고 위아래 컨트롤 비활성화
    textbox.setControlsVisibility({
      mt: false, // top middle (위 중앙)
      mb: false, // bottom middle (아래 중앙)
      ml: true, // left middle (왼쪽 중앙)
      mr: true, // right middle (오른쪽 중앙)
      mtr: false, // rotate (회전)
      tl: true, // top left (왼쪽 위)
      tr: true, // top right (오른쪽 위)
      bl: true, // bottom left (왼쪽 아래)
      br: true, // bottom right (오른쪽 아래)
    });

    // 터치 지원 개선 (별도 설정)
    (textbox as any).touchCornerSize = 8;

    // 캔버스에 추가
    this.canvas.offHistory();
    this.canvas.add(textbox);
    this.canvas.onHistory(true);

    // 추가된 텍스트 객체를 선택하고 편집 모드로 진입
    this.canvas.setActiveObject(textbox);
    textbox.enterEditing();

    // 텍스트가 초기 상태일 때 전체 선택
    textbox.selectAll();

    this.canvas.requestRenderAll();
  }

  setTool(tool: DrawingTool, option?: DrawingPenOption): void {
    // 현재 툴 옵션 저장 (툴 변경 시 사용)
    this._currentToolOption = option;

    this.canvas.isDrawingMode = tool === 'pen';
    // 텍스트 모드와 선택 모드에서 선택 활성화
    this.canvas.selection = tool === 'marquee' || tool === 'text';
    this.canvas.perPixelTargetFind = tool !== 'marquee';

    // 터치용 타겟 감지 허용 오차 조정
    if (tool === 'marquee' || tool === 'text') {
      this.canvas.targetFindTolerance = 15; // 선택 모드와 텍스트 모드에서 더 큰 터치 영역
    } else {
      this.canvas.targetFindTolerance = 10; // 기본 터치 영역
    }

    this.canvas.forEachObject((object) => {
      if (object.globalCompositeOperation === 'destination-out') {
        return;
      }

      // 텍스트 객체는 선택 모드와 텍스트 모드에서 선택 가능하도록 설정
      if (object.type === 'i-text' || object.type === 'textbox') {
        object.selectable = tool === 'marquee' || tool === 'text';
        object.evented = tool === 'marquee' || tool === 'text';

        if (tool === 'marquee' || tool === 'text') {
          // 선택 모드와 텍스트 모드에서 텍스트 전용: 확대축소 및 회전 컨트롤 활성화
          (object as any).hasControls = true;
          (object as any).hasBorders = true;
          (object as any).hasRotatingPoint = true;
          (object as any).lockUniScaling = false; // 비율 고정 해제
          // 터치 지원 개선
          (object as any).touchCornerSize = 24; // 터치용 큰 코너 크기
        }
      } else {
        // 선택 모드에서만 다른 객체들 선택 가능
        object.selectable = tool === 'marquee';
        object.evented = tool === 'marquee';

        // 선택 가능한 객체에 터치 지원 추가
        if (object.selectable) {
          (object as any).touchCornerSize = 24; // 터치용 큰 코너 크기
          (object as any).hasRotatingPoint = true;
        }
      }
    });

    if (tool !== 'marquee' && tool !== 'text') {
      this.clearSelection();
    }

    this._isEraserMode = tool === 'eraser';
    this._isTextMode = tool === 'text';
    this._isSelectionMode = tool === 'marquee';

    // 선택 모드에서 터치 최적화
    if (this._isSelectionMode) {
      // 모바일에서 스크롤 방지
      document.body.style.touchAction = 'none';
      document.body.style.overflow = 'hidden';
    } else {
      // 다른 모드에서는 스크롤 허용
      document.body.style.touchAction = 'auto';
      document.body.style.overflow = 'auto';
    }

    if (tool === 'pen') {
      this._pencilBrush.width = penWidthRelativeToViewportScale(
        option?.width ?? 1,
        this.viewportScale
      );
      this._pencilBrush.color = option?.color ?? '#000';
      this._pencilBrush.forceDrawStraightLine = option?.straight ?? false;

      this.canvas.freeDrawingBrush = this._pencilBrush;
    } else if (tool === 'text' && option) {
      // 텍스트 도구 옵션 저장
      this._currentTextOptions = {
        color: option.color ?? '#000000',
        fontSize: option.width ?? 16, // width를 fontSize로 사용
        fontFamily: option.fontFamily ?? 'Arial', // 기본값, 나중에 확장 가능
      };
    }
  }

  /**
   * 캔버스의 모든 일반 텍스트 객체를 편짓 가능한 텍스트로 변환
   */
  convertAllTextToEditable(): void {
    const objects = this.canvas.getObjects();
    const textObjects = objects.filter((obj) => obj.type === 'text');

    textObjects.forEach((textObj) => {
      const editableText = this._convertToEditableText(textObj, '');
      if (editableText) {
        this.canvas.remove(textObj);
        this.canvas.add(editableText);
      }
    });

    this.canvas.requestRenderAll();
  }

  resize(): void {
    const pageSize = {
      width: this.element.offsetWidth,
      height: this.element.offsetHeight,
    };

    if (pageSize.width === 0 || pageSize.height === 0) {
      // console.warn('invalid page size');
      return;
    }

    if (!this.canvas.isEmpty()) {
      const scale = (fabric.util as any).findScaleToFit(
        this.getDrawingSize(),
        pageSize
      );

      this.viewportScale = scale || 1;

      this._pencilBrush.width = penWidthRelativeToViewportScale(
        this._pencilBrush.width ?? 1,
        this.viewportScale
      );
    }

    this.canvas.setDimensions(pageSize);
  }

  // 선택된 텍스트 객체의 색상 업데이트
  updateSelectedTextColor(color: string): void {
    const activeObject = this.canvas.getActiveObject();

    if (activeObject && activeObject.type === 'textbox') {
      // 커서 너비를 0으로 설정하여 보이지 않게 함
      (activeObject as any).set('cursorWidth', 0);

      (activeObject as any).set('fill', color);
      this.canvas.requestRenderAll();
      this.canvas.onHistory(); // 히스토리에 추가
    }
  }

  // 선택된 텍스트 객체의 폰트 크기 업데이트
  updateSelectedTextFontSize(fontSize: number): void {
    const activeObject = this.canvas.getActiveObject();

    if (activeObject && activeObject.type === 'textbox') {
      // 커서 너비를 0으로 설정하여 보이지 않게 함
      (activeObject as any).set('cursorWidth', 0);

      // 현재 뷰포트 스케일에 맞게 폰트 크기 조정
      const scaledFontSize = fontSize / this.viewportScale;
      (activeObject as any).set('fontSize', scaledFontSize);
      this.canvas.requestRenderAll();
      this.canvas.onHistory(); // 히스토리에 추가
    }
  }

  // 선택된 텍스트 객체의 폰트 패밀리 업데이트
  updateSelectedTextFontFamily(fontFamily: string): void {
    const activeObject = this.canvas.getActiveObject();

    if (activeObject && activeObject.type === 'textbox') {
      // 커서 너비를 0으로 설정하여 보이지 않게 함
      (activeObject as any).set('cursorWidth', 0);

      (activeObject as any).set('fontFamily', fontFamily);
      this.canvas.requestRenderAll();
      this.canvas.onHistory(); // 히스토리에 추가
    }
  }

  // 커스텀 이동 컨트롤 추가 메서드
  private _addCustomMoveControl(textbox: fabric.Textbox): void {
    // 이동 잠금 해제 버튼 컨트롤 정의
    const moveControl = new fabric.Control({
      x: 0, // 오른쪽 중앙에 위치
      y: -0.5, // 위쪽 중앙에 위치
      offsetY: -0.4,
      cursorStyle: 'pointer',
      actionHandler: this._handleControlAction.bind(this),
      mouseUpHandler: this._handleControlActionComplete.bind(this),
      render: this._renderMoveControl.bind(this),
      actionName: 'toggleMovement',
    });

    // 커스텀 컨트롤 추가
    (textbox as any).controls.move = moveControl;
  }

  // 새로운 컨트롤 액션 핸들러
  private _handleControlAction(
    eventData: any,
    transform: any,
    x: number,
    y: number
  ): boolean {
    const target = transform.target;

    // 이벤트 타입에 따른 처리
    if (
      eventData.type === 'mousemove' ||
      eventData.type === 'touchmove' ||
      eventData.type === 'penmove'
    ) {
      // textbox를 panning 가능하게 설정
      target.set({
        lockMovementX: false,
        lockMovementY: false,
      });

      // 캔버스 좌표로 변환
      const actualWidth = target.getScaledWidth(); // 너비 변환했을때 실제 텍스트 필기 박스의 너비

      target.set({
        left: x - actualWidth / 2,
        top: y,
      });

      this.canvas.requestRenderAll();
    }

    return true; // 이벤트 처리 완료
  }

  // 컨트롤 액션 완료 핸들러 추가
  private _handleControlActionComplete(
    eventData: any,
    transform: any,
    x: number,
    y: number
  ): boolean {
    const target = transform.target;

    // 이동 잠금 다시 활성화
    target.set({
      lockMovementX: true,
      lockMovementY: true,
    });

    this.canvas.requestRenderAll();
    return true; // 이벤트 처리 완료
  }

  // 이동 컨트롤 렌더링 메서드
  private _renderMoveControl(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    styleOverride: any,
    fabricObject: fabric.Object
  ): void {
    const centerX = left;
    const centerY = top;

    // 배경 원 그리기
    ctx.save();
    ctx.beginPath();
    ctx.arc(centerX, centerY, 2, 0, 2 * Math.PI); // 반지름 4px
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.strokeStyle = '#0047FF';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.restore();
  }

  /**
   * 히스토리 undo/redo 후 텍스트 객체의 스타일을 복원
   */
  private _restoreTextObjectStyles(): void {
    this.canvas.getObjects().forEach((object) => {
      if (object.type === 'textbox') {
        // 텍스트 객체의 스타일 복원
        (object as any).set({
          hasControls: true,
          hasBorders: true,
          hasRotatingPoint: true,
          lockUniScaling: false,
          fontFamily: 'pretendard variable',
          editingBorderColor: '#0047FF',
          cursorColor: '#282828',
          cursorWidth: 2,
          borderColor: '#0047FF',
          cornerColor: '#0047FF',
          cornerSize: 3,
          transparentCorners: false,
          lockMovementX: true,
          lockMovementY: true,
          splitByGrapheme: true,
          touchCornerSize: 8,
        });

        // 컨트롤 가시성 설정
        (object as any).setControlsVisibility({
          mt: false,
          mb: false,
          ml: true,
          mr: true,
          mtr: false,
          tl: true,
          tr: true,
          bl: true,
          br: true,
        });

        // 커스텀 이동 컨트롤 추가
        this._addCustomMoveControl(object as fabric.Textbox);
      }
    });

    this.canvas.requestRenderAll();
  }
}
